// Reading the deep link the Nav failure mail sends finance.
//
// `CRMSendTimelogsToNav` mails finance the moment Nav rejects a timelog, and
// that mail now carries a link straight to the row:
//
//   https://dashboard.mw.zrm.dk/?tab=nav-queue&status=rejected&id=<timelog id>
//
// The dashboard has no router (plain CRA, UserManagement is mounted directly),
// and nginx serves index.html for every path, so a query string is the whole
// mechanism. Kept in its own module because two components read it and because
// a pure function is testable without rendering either of them.

// Only tabs that exist. An unknown value is ignored rather than trusted: the
// link comes in by email, so it is the one input here a stranger could forge,
// and the worst it should ever do is open the default tab.
const KNOWN_TABS = new Set(['users', 'caps', 'access', 'reminders', 'nav-queue', 'release-notes']);
const KNOWN_STATUSES = new Set(['pending', 'rejected']);
// Which app's release notes are open. Same reasoning as the tabs: forged or
// stale, the worst it does is open the first app.
const KNOWN_APPS = new Set(['calendar', 'dashboard']);

export function parseDeepLink(search) {
    let params;
    try {
        params = new URLSearchParams(search || '');
    } catch {
        return {};
    }

    const tab = params.get('tab');
    const status = params.get('status');
    const id = params.get('id');
    const app = params.get('app');

    return {
        tab: KNOWN_TABS.has(tab) ? tab : undefined,
        status: KNOWN_STATUSES.has(status) ? status : undefined,
        app: KNOWN_APPS.has(app) ? app : undefined,
        // A CRM record id is digits only. Anything else is not one, and not
        // echoing it into the DOM costs nothing.
        id: id && /^\d+$/.test(id) ? id : undefined,
        // Only the off switch is carried: stuck-only is the default, so the
        // URL stays short for the common case.
        stuck: params.get('stuck') === '0' ? '0' : undefined,
    };
}

// Writes the current screen back into the address bar, so the URL can be copied
// and shared and a refresh lands in the same place. replaceState rather than
// pushState: a tab switch is not a page the back button should walk through.
export function writeDeepLink({ tab, status, stuck, id, app } = {}) {
    if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;

    const params = new URLSearchParams();
    if (tab) params.set('tab', tab);
    if (status) params.set('status', status);
    if (stuck) params.set('stuck', stuck);
    if (app) params.set('app', app);
    // Never invented, only carried while it still points at something the
    // reader is looking at. The caller decides when that stops being true.
    if (id) params.set('id', id);

    const query = params.toString();
    window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
}

export function currentDeepLink() {
    return parseDeepLink(typeof window === 'undefined' ? '' : window.location.search);
}
